import { resolverColor } from "../../shared/paletaDelTema";
import { AMBIENTES, FOTO_SIN_AMBIENTE } from "../../shared/ambiente";

/* Los 13 temas del rediseño (UAT), en un solo lugar: los usan el selector del
 * armazón nuevo y el conmutador de armazón. Cada uno es un bloque de variables
 * en `assets/css/temas.css`, activado por `data-tema` en la raíz. */
export const TEMAS_UAT = [
  ["industrial", "Industrial"],
  ["torre", "Torre de control"],
  ["papel", "Papel"],
  ["suave", "Suave"],
  ["playa", "Playa"],
  ["montana", "Montaña"],
  ["nieve", "Nieve"],
  ["neon", "Neón"],
  ["desierto", "Desierto"],
  ["espacio", "Espacio"],
  ["arcade", "Arcade"],
  ["sakura", "Sakura"],
  ["otono", "Otoño"],
  // Los de club (`temas-clubes.css`): los colores, no los escudos.
  ["river", "River"],
  ["boca", "Boca"],
  ["racing", "Racing"],
  // Lo pidió Lautaro (Satelital, 08/10), y con la misma regla: el rojo y el
  // azul y unas arañas, nada del personaje. El nombre es sólo el que se ve;
  // el id no lo nombra.
  // (Desde el 09/10 los dos llevan logo y personaje: decisión de Igna.)
  ["aracnido", "Spider-Man"],
  // Y su contraparte, que pidió el mismo día después de ver el otro: negro
  // brillante, blanco hueso y el simbionte que asoma por los bordes. Misma
  // regla: nada del personaje, y el id tampoco lo nombra. (Tiene una
  // variante con la cara de fondo: ver `VARIANTES_DE_TEMA`.)
  ["simbionte", "Venom"],
  // Igna (08/10): con personajes y el emblema de la U.A., «como hicimos con
  // los escudos».
  ["mha", "My Hero Academia"],
  // Guillermo (Portería, 13/10): «Temas de X Men y Avengers (Marvel) y
  // Batman, Dark Knight (DC)». Igna: «vamos por eso». Con logo, como los de
  // arriba; los ids no nombran al personaje, como `aracnido` y `simbionte`.
  ["mutante", "X-Men"],
  ["vengadores", "Avengers"],
  ["caballero", "Batman"],
  // Igna (20/10): «me gustaría intentar hacer una temática Disney». Las
  // imágenes las generó él; el id tampoco nombra a nadie.
  ["magia", "Disney"],
  // Igna (23/10): «quiero crear uno de Resident Evil + Umbrella corps».
  // Imágenes suyas; el id es la Colmena, el laboratorio de Umbrella.
  ["colmena", "Resident Evil"],
  ["isla", "Jurassic Park"],
  // Igna (02/11): «Quiero 3 themes nuevos. 1 : Windows 95, 2 : MacOS, 3: Como
  // si fuese un programa en DOS». Imágenes suyas; los ids no nombran la marca.
  ["ventanas", "Windows 95"],
  ["cristal", "macOS"],
  ["consola", "DOS"],
  // Igna (02/11) eligió Navidad entre las ideas de temas nuevos. Imágenes
  // suyas.
  ["navidad", "Navidad"],
  // Lautaro (Satelital, 03/11): «¿podrás crear un tema de los Beatles?», y
  // en el ticket 621, «tipo el álbum Rubber Soul». Homenaje al clima de ese
  // disco con dibujos propios; el id no nombra a la banda.
  ["cuarteto", "Beatles"],
];

/* ESTÁNDAR: EL HUB COMO ES SIN TEMA.
 *
 * Igna (08/10): «debería existir el theme "standard", ¿no?». Sí, y es el que
 * hace falta para llevar esto a producción: no es un tema más —no tiene
 * bloque en `temas.css`, ni emblemas, ni ambiente— sino la AUSENCIA de tema.
 * Con Estándar no se pone `data-tema`, y como todo lo de la rama cuelga de
 * ese atributo, el hub queda como en producción: el rail de siempre, y el
 * claro/oscuro y el color de acento que cada uno eligió en Preferencias.
 *
 * Por eso va aparte de `TEMAS_UAT` (que es la lista de los que SÍ tienen
 * bloque, y varios tests la recorren pidiéndole a cada uno su hoja) y es el
 * de arranque: quien nunca eligió nada ve lo mismo que veía. */
export const TEMA_ESTANDAR = "estandar";
export const TEMA_DEFECTO = TEMA_ESTANDAR;

/* LAS VARIANTES: UN TEMA, OTRO FONDO.
 *
 * Venom tuvo dos fondos y los dos tienen quien los quiera: la cara quieta que
 * pidió Igna (09/10, #658: «poneme la cara de fondo, sin goo que se mueva») y
 * el simbionte con tentáculos enredados que pidió Lautaro (13/10: «un fondo
 * más como el que tenía antes… y más Venom sin su cara»). El tema es el mismo
 * para todos, así que Igna (13/10) eligió que estén los dos en el selector:
 * «Venom» es el de Lautaro —quien ya lo tenía elegido sigue con ése— y
 * «Venom (cara)», el de #658.
 *
 * Una variante NO es un tema más: comparte TODO con su tema —colores, letras,
 * emblemas, logo, lo que viaja a los paneles de FlotaBot— y cambia sólo lo
 * que cuelga de `data-variante` en la raíz (en `armazon-uat.css`). Por eso no
 * va en `TEMAS_UAT`, que es la lista de los que tienen hoja propia y que los
 * tests recorren pidiéndole a cada uno la suya. En la raíz, `data-tema` dice
 * el tema (`simbionte`) y `data-variante` la variante (`cara`); lo que se
 * guarda —en el navegador y en la cuenta— es el id de la opción.
 *
 * Y la cara va quieta: `AmbienteTema` recibe el id de la opción, y en
 * `AMBIENTES` no hay ninguno con el id de una variante, así que no arranca
 * el lienzo de los zarcillos y las trenzas. */
export const VARIANTES_DE_TEMA = {
  "simbionte-cara": { tema: "simbionte", variante: "cara", nombre: "Venom (cara)" },
};
/* El tema que va en `data-tema` para una opción (la opción misma, si no es
 * una variante), y la variante que va en `data-variante` (o `null`). */
export const temaDeLaOpcion = (id) => VARIANTES_DE_TEMA[id]?.tema ?? id;
export const varianteDeLaOpcion = (id) => VARIANTES_DE_TEMA[id]?.variante ?? null;

/* Lo que se le ofrece a la persona para elegir: Estándar primero, y cada
 * variante al lado de su tema. */
export const OPCIONES_DE_TEMA = [
  [TEMA_ESTANDAR, "Estándar"],
  ...TEMAS_UAT.flatMap((t) => [
    t,
    ...Object.entries(VARIANTES_DE_TEMA)
      .filter(([, v]) => v.tema === t[0])
      .map(([id, v]) => [id, v.nombre]),
  ]),
];

/* Dónde queda guardado el tema elegido. Es del NAVEGADOR y no de la persona:
 * por eso lo puede leer la pantalla de ingreso, que todavía no sabe quién
 * entra pero sí con qué tema se usó el hub la última vez en esta máquina. */
export const CLAVE_TEMA = "uat-tema";

/* CUÁN OPACAS SON LAS TARJETAS, en los temas con foto de fondo. Igna (10/10)
 * pidió 60 % para todas y, mejor, poder elegirlo. Es un porcentaje entre
 * `VIDRIO_MIN` y 100; por debajo la letra ya no se lee sobre las fotos. */
export const CLAVE_VIDRIO = "uat-vidrio";
export const VIDRIO_DEFECTO = 60;
export const VIDRIO_MIN = 40;
export function vidrioValido(v) {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && v !== null && v !== "" ? Math.min(100, Math.max(VIDRIO_MIN, n)) : null;
}
export function vidrioGuardado() {
  try {
    return vidrioValido(localStorage.getItem(CLAVE_VIDRIO)) ?? VIDRIO_DEFECTO;
  } catch {
    return VIDRIO_DEFECTO;
  }
}

export function temaGuardado() {
  try {
    const t = localStorage.getItem(CLAVE_TEMA);
    return OPCIONES_DE_TEMA.some(([id]) => id === t) ? t : TEMA_DEFECTO;
  } catch {
    return TEMA_DEFECTO;
  }
}

/* Los que tienen fondo oscuro. Lo necesita `LayoutSwitcher` para decirle al
 * resto del hub en qué modo está: decenas de reglas viejas preguntan por
 * `data-theme="dark"` (la fila enfocada de Presidencia, la pastilla de
 * Transcan…) y no saben nada de `data-tema`. Una variante es oscura si su
 * tema lo es: se pregunta con `temaOscuro`, que recibe el id de la opción. */
export const TEMAS_OSCUROS = ["torre", "neon", "espacio", "arcade", "boca", "simbionte", "vengadores", "caballero", "magia", "colmena", "isla", "cristal", "consola"];
export const temaOscuro = (opcion) => TEMAS_OSCUROS.includes(temaDeLaOpcion(opcion));

/* ── LO QUE DEL TEMA VIAJA A LOS PANELES DE FLOTABOT ──────────────────────────
 *
 * Los paneles son otra aplicación adentro de un iframe: no ven `temas.css`. El
 * hub les manda el nombre del tema y el valor de estas variables, y FlotaBot
 * las traduce a sus propios tokens (`ui/estilo.css`, allá). Así los temas
 * viven en un solo lugar —acá— y sumar uno no toca el otro repo.
 *
 * La lista es el contrato entre los dos lados. Las siete primeras son las que
 * FlotaBot exige para vestirse; sin alguna, deja el panel como estaba. */
export const TOKENS_PANEL = [
  "bg", "surf", "ink", "ink2", "hair", "acc", "accInk",
  "surf2", "ink3", "line", "bw", "bw2", "r", "rs", "shd", "shdS",
  "fTit", "titW", "titCase", "titLS", "fBody", "fNum", "fMono",
  "crit", "warn", "ok", "info", "critTxt", "warnTxt", "selBg",
  "thBg", "thInk", "bandBg", "bandInk",
];

/* Las letras de los temas. Son los mismos `@import` de `temas.css` (hay un
 * test que los compara): el panel las pide por su cuenta, porque una fuente
 * cargada en el hub no existe adentro del iframe. */
export const FUENTES_TEMAS = [
  "https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&family=Barlow+Condensed:wght@600;700;800&family=IBM+Plex+Mono:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600&family=Chakra+Petch:wght@500;600;700&family=JetBrains+Mono:wght@400;500;600&family=Newsreader:opsz,wght@6..72,400;6..72,500;6..72,600&family=Public+Sans:wght@400;500;600;700&family=Outfit:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap",
  "https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Nunito+Sans:wght@400;600;700&family=Bitter:wght@600;700&family=Source+Sans+3:wght@400;600;700&family=Space+Mono:wght@400;700&family=Josefin+Sans:wght@300;600;700&family=Audiowide&family=Space+Grotesk:wght@400;500;700&family=Share+Tech+Mono&family=Alfa+Slab+One&family=Work+Sans:wght@400;500;700&family=Michroma&family=Exo+2:wght@200;400;600&display=swap",
  "https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Pixelify+Sans:wght@400;600&family=Shippori+Mincho:wght@500;600;700&family=Zen+Kaku+Gothic+New:wght@400;700&family=Young+Serif&family=Karla:wght@400;600;700&family=VT323&family=Inter:wght@400;500;600;700&family=Righteous&display=swap",
];

/* De dos colores, el primero si es oscuro y si no el segundo. Para saber si
 * es oscuro hay que resolverlo (puede venir como `rgba(…)` o `color-mix(…)`);
 * donde no se puede —sin lienzo— no se manda nada y el panel queda como
 * estaba. `resolver` se pasa para poder probarlo. */
export function barraOscura(fondo, tinta, resolver = resolverColor) {
  const luz = (c) => {
    const rgb = resolver(c);
    return rgb ? (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255 : null;
  };
  const lf = luz(fondo);
  if (lf === null) return null;
  if (lf < 0.42) return fondo;
  const lt = luz(tinta);
  return lt !== null && lt < 0.42 ? tinta : null;
}

/* El tema que tiene puesto el hub, listo para mandarle a un panel: el nombre y
 * los valores YA RESUELTOS (lo que el navegador calculó, no la regla). Devuelve
 * `null` si no hay tema —producción—, y entonces al panel le llega sólo
 * claro/oscuro, como siempre. */
export function estiloParaPanel() {
  if (typeof document === "undefined") return null;
  const raiz = document.documentElement;
  const estilo = raiz.getAttribute("data-tema");
  if (!estilo) return null;
  const calculado = window.getComputedStyle(raiz);
  const vars = {};
  TOKENS_PANEL.forEach((n) => {
    const v = calculado.getPropertyValue("--" + n).trim();
    if (v) vars[n] = v;
  });
  // LA BARRA DE LAS PANTALLAS DE FLOTABOT. Tienen una barra propia arriba con
  // la letra blanca escrita a mano, así que el color que se les mande tiene
  // que ser OSCURO siempre: el de la barra del hub si lo es, y si no —Papel,
  // Suave, Nieve, Sakura tienen la barra clara— la tinta del tema. Antes no
  // se mandaba nada y quedaba su azul grafito adentro de cualquier tema.
  const barra = barraOscura(
    calculado.getPropertyValue("--hdrBg").trim(),
    calculado.getPropertyValue("--ink").trim(),
  );
  if (barra) vars.barra = barra;
  // LA OPACIDAD DE LAS TARJETAS, para que las de los paneles sigan al
  // deslizador de Preferencias. Sólo en los temas con foto o ambiente —que es
  // donde las tarjetas del hub son translúcidas—, y sólo si no quedaron
  // macizas (interruptor «Transparencia» apagado, o «menos transparencia» en
  // el sistema, que dejan el valor en 100 %).
  const vidrio = calculado.getPropertyValue("--pf-vidrio-alfa").trim();
  const conFondo = estilo in AMBIENTES || FOTO_SIN_AMBIENTE.includes(estilo);
  const macizo = document.body?.classList.contains("sin-blur");
  if (conFondo && !macizo && /^\d{1,3}%$/.test(vidrio) && vidrio !== "100%") vars.vidrio = vidrio;
  return { estilo, vars, fuentes: FUENTES_TEMAS };
}
